// Inbox's own pages in Blueprint (pages/ux-full inbox/approval and inbox/notifications): what the
// request and the notifications say, from the real answers (approvals.list's row and
// notifications.list's rows), in the words ApprovalsInbox and the bell already use; the rows as
// RowList's.
import { dateTimeText } from '@flowology/kernel';
import type { AgentRequestProps, NoteListProps } from '@flowology/ui-web';
import type { Row } from '@flowology/ui-web/fl';
import { MONEY_KIND_LABEL, agentName, inputRows, moneyKindOf } from '../books/model.ts';
import type { Approval } from '../books/types.ts';
import { groupNotes, noteTime, type Note } from '../shared/notifications-model.ts';

/** What a person decided on a request (approvals.decide's input). */
export interface Decision {
  decision: 'approve' | 'decline';
  /** Approve, and let the person's own AI tool act as them for trustHours (approvals.decide's trustConnection). */
  trust?: boolean;
  /** A decline's note, which the agent reads next time. */
  note?: string;
}

/** How long approving "act as me" lets the person's own AI tool act as them (approvals.decide's TRUST_HOURS). */
export const TRUST_HOURS = 12;

/** The run that asked found instructions in untrusted text (ApprovalsInbox's isFlagged). */
const UNTRUSTED_FLAG = 'instructions_in_untrusted_input';
export const flagged = (a: Pick<Approval, 'explanation' | 'flags'>) =>
  !!a.flags?.includes(UNTRUSTED_FLAG) || !!a.explanation?.startsWith('Instructions found');

/** What an approved request's run came to (ApprovalsInbox's runResult): it ran, it's running, or why not. */
function ran(a: Pick<Approval, 'status' | 'outcome'>): {
  ran: 'done' | 'running' | 'failed' | null;
  failed: string | null;
} {
  if (a.status !== 'approved') return { ran: null, failed: null };
  const o = a.outcome as { ok?: boolean; error?: { message?: string } } | null;
  if (!o || typeof o.ok !== 'boolean') return { ran: 'running', failed: null };
  return o.ok
    ? { ran: 'done', failed: null }
    : { ran: 'failed', failed: o.error?.message ?? 'it was refused' };
}

/**
 * One approvals.list row as AgentRequest's props. `names` are the words for the ids in its input (its
 * labels, and invoice numbers read since); `stopped` is why the agent that asked is stopped, if it is,
 * which locks it the way ApprovalsInbox does.
 */
export function requestProps(
  a: Approval,
  {
    timeZone,
    names = new Map(),
    stopped = null,
  }: { timeZone: string; names?: ReadonlyMap<string, string>; stopped?: string | null },
): AgentRequestProps {
  const tool = a.agent.startsWith('user:');
  const kind = moneyKindOf(a.action);
  const words = new Map([...names, ...Object.entries(a.labels ?? {})]);
  const locked =
    a.cannotDecide ??
    (stopped && a.status === 'pending'
      ? `Nothing can run as the ${agentName(a.agent).toLowerCase()} now: ${stopped.charAt(0).toLowerCase()}${stopped.slice(1).replace(/\.$/, '')}.`
      : null);
  return {
    id: a.id,
    title: a.actionTitle,
    action: a.action,
    asker: {
      name: tool ? (a.canTrustConnection ? 'Your AI tool, as you' : 'An AI tool') : agentName(a.agent),
      tool,
    },
    asked: dateTimeText(a.createdAt, timeZone),
    expires: a.status === 'pending' ? dateTimeText(a.expiresAt, timeZone) : null,
    explanation: a.explanation,
    flagged: flagged(a),
    money: kind ? MONEY_KIND_LABEL[kind] : null,
    rows:
      a.input === null
        ? null
        : inputRows(a.input, words, timeZone).map((r) => ({ label: r.label, text: r.text, exact: r.exact })),
    state: a.status,
    decided:
      a.status === 'pending'
        ? null
        : { at: a.decidedAt ? dateTimeText(a.decidedAt, timeZone) : null, note: a.note, ...ran(a) },
    cannotDecide: locked,
    canTrust: a.status === 'pending' && !!a.canTrustConnection,
    trustHours: TRUST_HOURS,
  };
}

/** The one to show: the one asked for while it's there, else the oldest still waiting that this person may decide. */
export function shownRequest(list: readonly Approval[], wanted: string | null): Approval | null {
  const asked = wanted ? list.find((a) => a.id === wanted) : undefined;
  if (asked) return asked;
  const oldest = [...list].sort((x, y) => x.createdAt.localeCompare(y.createdAt));
  return oldest.find((a) => a.cannotDecide === null) ?? oldest[0] ?? null;
}

/** notifications.list's rows as NoteList's props, grouped as the bell groups them. */
export function noteListProps(notes: readonly Note[], now: Date, timeZone: string): NoteListProps {
  return {
    groups: groupNotes(notes, now, timeZone).map((g) => ({
      key: g.key,
      label: g.label,
      notes: g.notes.map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body,
        time: noteTime(n.createdAt, now, timeZone),
        urgent: n.urgent,
        read: n.read,
      })),
    })),
  };
}

const SAID: Record<AgentRequestProps['state'], string> = {
  pending: 'Waiting for you',
  approved: 'Approved',
  declined: 'Declined',
  expired: 'Lapsed',
};

/** How a decided request was decided, and what its run came to: "Approved Nov 4, 3:07 a.m. It ran as you." */
export function decidedText(r: AgentRequestProps): string {
  const d = r.decided;
  const ran =
    d?.ran === 'done'
      ? r.asker.tool
        ? ' It ran as you.'
        : ' It ran as the agent, with you as the approver.'
      : d?.ran === 'running'
        ? ' It is running.'
        : d?.ran === 'failed'
          ? ` It did not run: ${d.failed ?? 'it was refused'}.`
          : '';
  // a time ends in "a.m.": its own dot ends the sentence
  return `${SAID[r.state]}${d?.at ? ` ${d.at.replace(/\.$/, '')}` : ''}.${ran}`;
}

/** The other requests still waiting, one a row: what it would do (it opens it), who asked and when. */
export const waitingRows = (
  list: readonly Approval[],
  { timeZone, onOpen }: { timeZone: string; onOpen: (id: string) => void },
): Row[] =>
  list.map((a) => {
    const p = requestProps(a, { timeZone });
    return {
      id: a.id,
      name: p.title,
      onOpen: () => onOpen(a.id),
      // a time ends in "a.m.": its own dot ends the sentence before the next
      lines: [
        {
          text: `${p.asker.name}, asked ${p.cannotDecide ? `${p.asked.replace(/\.$/, '')}. Not yours to decide` : p.asked}`,
        },
      ],
    };
  });
